import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { Bank, Code, GraduationCap, Trophy, PuzzlePiece, Brain, BookOpen, Newspaper, Function as FunctionIcon, Flask, Sparkle, Tag } from "@phosphor-icons/react";

// Built-in fallback (matches backend DEFAULT_EXAM_CATEGORIES). Used only if
// the API call fails so filters and badges still render something sensible.
export const DEFAULT_EXAM_CATEGORIES = [
  { slug: "banking", label: "Banking" },
  { slug: "it", label: "IT" },
  { slug: "general_knowledge", label: "General Knowledge" },
  { slug: "competitive", label: "Competitive" },
  { slug: "reasoning", label: "Reasoning" },
  { slug: "aptitude", label: "Aptitude" },
  { slug: "english", label: "English" },
  { slug: "current_affairs", label: "Current Affairs" },
  { slug: "mathematics", label: "Mathematics" },
  { slug: "science", label: "Science" },
  { slug: "other", label: "Other" },
];

// Visual mapping per known slug. Custom slugs fall through to a rotating
// palette based on a stable hash so tiles still look intentional.
const CATEGORY_STYLES = {
  banking:          { icon: Bank,         color: "emerald" },
  it:               { icon: Code,         color: "blue" },
  general_knowledge:{ icon: GraduationCap,color: "indigo" },
  competitive:      { icon: Trophy,       color: "amber" },
  reasoning:        { icon: PuzzlePiece,  color: "violet" },
  aptitude:         { icon: Brain,        color: "rose" },
  english:          { icon: BookOpen,     color: "sky" },
  current_affairs:  { icon: Newspaper,    color: "orange" },
  mathematics:      { icon: FunctionIcon, color: "teal" },
  science:          { icon: Flask,        color: "cyan" },
  other:            { icon: Sparkle,      color: "zinc" },
};

const FALLBACK_PALETTE = ["fuchsia", "lime", "amber", "emerald", "sky", "rose", "indigo", "cyan"];

// Tailwind classes per color. Kept explicit so JIT includes them in the bundle.
export const CATEGORY_TONE = {
  emerald: { bg: "bg-emerald-50", ring: "border-emerald-200", chip: "bg-emerald-100 text-emerald-700", iconBg: "bg-emerald-500/10", iconFg: "text-emerald-700" },
  blue:    { bg: "bg-blue-50",    ring: "border-blue-200",    chip: "bg-blue-100 text-blue-700",       iconBg: "bg-blue-500/10",    iconFg: "text-blue-700" },
  indigo:  { bg: "bg-indigo-50",  ring: "border-indigo-200",  chip: "bg-indigo-100 text-indigo-700",   iconBg: "bg-indigo-500/10",  iconFg: "text-indigo-700" },
  amber:   { bg: "bg-amber-50",   ring: "border-amber-200",   chip: "bg-amber-100 text-amber-800",     iconBg: "bg-amber-500/10",   iconFg: "text-amber-700" },
  violet:  { bg: "bg-violet-50",  ring: "border-violet-200",  chip: "bg-violet-100 text-violet-700",   iconBg: "bg-violet-500/10",  iconFg: "text-violet-700" },
  rose:    { bg: "bg-rose-50",    ring: "border-rose-200",    chip: "bg-rose-100 text-rose-700",       iconBg: "bg-rose-500/10",    iconFg: "text-rose-700" },
  sky:     { bg: "bg-sky-50",     ring: "border-sky-200",     chip: "bg-sky-100 text-sky-700",         iconBg: "bg-sky-500/10",     iconFg: "text-sky-700" },
  orange:  { bg: "bg-orange-50",  ring: "border-orange-200",  chip: "bg-orange-100 text-orange-700",   iconBg: "bg-orange-500/10",  iconFg: "text-orange-700" },
  teal:    { bg: "bg-teal-50",    ring: "border-teal-200",    chip: "bg-teal-100 text-teal-700",       iconBg: "bg-teal-500/10",    iconFg: "text-teal-700" },
  cyan:    { bg: "bg-cyan-50",    ring: "border-cyan-200",    chip: "bg-cyan-100 text-cyan-700",       iconBg: "bg-cyan-500/10",    iconFg: "text-cyan-700" },
  zinc:    { bg: "bg-zinc-50",    ring: "border-zinc-200",    chip: "bg-zinc-200 text-zinc-700",       iconBg: "bg-zinc-500/10",    iconFg: "text-zinc-700" },
  fuchsia: { bg: "bg-fuchsia-50", ring: "border-fuchsia-200", chip: "bg-fuchsia-100 text-fuchsia-700", iconBg: "bg-fuchsia-500/10", iconFg: "text-fuchsia-700" },
  lime:    { bg: "bg-lime-50",    ring: "border-lime-200",    chip: "bg-lime-100 text-lime-800",       iconBg: "bg-lime-500/10",    iconFg: "text-lime-700" },
};

function hashSlug(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i += 1) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return h;
}

export function categoryVisual(slug) {
  if (!slug) return { Icon: Tag, tone: CATEGORY_TONE.zinc, color: "zinc" };
  if (CATEGORY_STYLES[slug]) {
    const { icon, color } = CATEGORY_STYLES[slug];
    return { Icon: icon, tone: CATEGORY_TONE[color], color };
  }
  const color = FALLBACK_PALETTE[hashSlug(slug) % FALLBACK_PALETTE.length];
  return { Icon: Tag, tone: CATEGORY_TONE[color] || CATEGORY_TONE.zinc, color };
}

// Fallback map for cases where a category was deleted after being assigned.
const FALLBACK_LABELS = DEFAULT_EXAM_CATEGORIES.reduce((m, c) => {
  m[c.slug] = c.label;
  return m;
}, {});

export function humaniseSlug(slug) {
  if (!slug) return "Uncategorised";
  if (FALLBACK_LABELS[slug]) return FALLBACK_LABELS[slug];
  return String(slug).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * useExamCategories() — loads categories for the caller's institution.
 * Returns { categories, byId, labelFor, loading, reload }.
 */
export function useExamCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/exam-categories");
      setCategories(r.data || []);
    } catch {
      setCategories(
        DEFAULT_EXAM_CATEGORIES.map((c) => ({
          category_id: `sys_${c.slug}`,
          slug: c.slug,
          label: c.label,
          is_system: true,
          institution_id: null,
        })),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bySlug = categories.reduce((m, c) => {
    m[c.slug] = c;
    return m;
  }, {});

  const labelFor = (slug) => (bySlug[slug]?.label) || humaniseSlug(slug);

  return { categories, bySlug, labelFor, loading, reload: load };
}

/**
 * usePublicExamCategories() — loads the AGGREGATED public list (no auth).
 * Used by the Landing page & guest catalog. Returns [{slug, label, count}].
 */
export function usePublicExamCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/exam-categories");
        setItems(r.data || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return { items, loading };
}
